// Importing a recipe from a link (feature spec 02): what the field accepts,
// and how the `recipe` function's answer becomes a saved recipe. The function
// returns raw ingredient lines; they are parsed here, with the same parser the
// rest of the app uses (recipe.ts), so amounts merge and scale like any other.

import type { Recipe, RecipeIngredient, RecipeSource, RecipeStep } from './supabase'
import { bareList, parseIngredient, type IngredientIssue } from './recipe'

export const LINK_ERRORS = {
  empty: 'Paste a link to a recipe page or a video first.',
  invalid: "That doesn't look like a link. Paste the full address of a recipe page or a video.",
} as const

/** The first web address in shared or copied text ("Look at this! https://vm.tiktok.com/…"). */
export function linkInText(text: string | null | undefined): string | null {
  const m = (text ?? '').match(/https?:\/\/[^\s<>"]+/i)
  return m ? m[0].replace(/[.,!?;:)\]]+$/, '') : null
}

/**
 * A link handed to the app from outside: `?import=<link>` (an iPhone Shortcut,
 * any "open with" link) or the Web Share Target's `url`/`text`/`title`
 * (Android's share sheet; TikTok and Instagram put the link in `text`).
 */
export function sharedLinkFrom(params: URLSearchParams): string | null {
  for (const key of ['import', 'url', 'text', 'title']) {
    const link = linkInText(params.get(key))
    if (link) return link
  }
  return null
}

/**
 * The platform a post's link is on, by name, for asking its caption (the
 * `recipe` function's needs_caption: Instagram behind its login wall, TikTok's
 * cut short); null for anything else, a recipe page included.
 */
export function postPlatform(url: string): 'Instagram' | 'TikTok' | 'YouTube' | null {
  try {
    const u = new URL(url)
    const host = u.hostname.toLowerCase()
    if (/(^|\.)instagram\.com$/.test(host)) return /^\/(p|reels?|tv)\/[^/]+/.test(u.pathname) ? 'Instagram' : null
    if (/(^|\.)tiktok\.com$/.test(host)) return 'TikTok'
    if (/(^|\.)(youtube\.com|youtu\.be)$/.test(host)) return 'YouTube'
    return null
  } catch {
    return null
  }
}

export type LinkCheck = { ok: true; url: string; host: string } | { ok: false; error: string }

/** A web address the importer can try: http(s), with a dotted host. "koket.se/x" gets https://. */
export function checkRecipeLink(input: string): LinkCheck {
  const raw = input.trim()
  if (!raw) return { ok: false, error: LINK_ERRORS.empty }
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`
  let url: URL
  try { url = new URL(withScheme) } catch { return { ok: false, error: LINK_ERRORS.invalid } }
  if ((url.protocol !== 'https:' && url.protocol !== 'http:') || !url.hostname.includes('.') || /\s/.test(raw)) {
    return { ok: false, error: LINK_ERRORS.invalid }
  }
  return { ok: true, url: withScheme, host: url.hostname.replace(/^www\./, '') }
}

/** What the `recipe` edge function answers (supabase/functions/recipe/extract.ts). */
export interface ImportedRecipe {
  title: string | null
  servings: number | null
  ingredients: string[]
  minutes?: number | null
  steps?: RecipeStep[]
  vegetarian?: true
  /** Where it came from; older function versions answer without it (a page). */
  source?: RecipeSource
  /** The page's aggregateRating, out of 5. */
  rating?: { value: number; count: number }
  /** The page's photo or the video's thumbnail, as base64 — resized and kept privately by the PWA. */
  image?: { data: string; type: string }
  /** A page with no ingredient list on it, read in part: what there was, and its text to copy from. */
  partial?: true
  pageText?: string
}

export type NewRecipe = Pick<Recipe,
  'title' | 'url' | 'servings' | 'minutes' | 'vegetarian' | 'ingredients' | 'steps' | 'source' | 'rating_value' | 'rating_count'>

/** A video platform's post: a recipe may come back without ingredients, to fill in by hand. */
export const isVideoSource = (s: RecipeSource | null | undefined) => s === 'youtube' || s === 'tiktok' || s === 'instagram'

/** The import's image as a Blob, for lib/photo.ts to resize; null when there is none or it doesn't decode. */
export function importedImage(image: ImportedRecipe['image']): Blob | null {
  if (!image || !/^image\/(jpeg|png|webp)$/.test(image.type)) return null
  try {
    const bin = atob(image.data)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return new Blob([bytes], { type: image.type })
  } catch {
    return null
  }
}

/** A saved ingredient beside the line it was read from and what was unclear about it (lib/importReview.ts). */
export type ImportedIngredient = RecipeIngredient & { raw: string; issues: IngredientIssue[] }

/**
 * The import's ingredients as recipeFromImport saves them, each with its line —
 * never saved. A line of bare names ("salt, svartpeppar") is one item each.
 */
export function importedIngredients(data: Pick<ImportedRecipe, 'ingredients'>): ImportedIngredient[] {
  return data.ingredients.flatMap(raw => (bareList(raw) ?? [raw]).map(line => {
    const p = parseIngredient(line)
    return { name: p.name, amount: p.amount, unit: p.unit, raw, issues: p.issues }
  })).filter(g => g.name)
}

/** Pasted text has no link: no source, and no site to name it after. */
export function recipeFromImport(data: ImportedRecipe, url: string | null): NewRecipe {
  const host = url == null ? null : (() => { try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url } })()
  return {
    title: data.title?.trim() || host || 'Untitled recipe',
    url,
    servings: data.servings ?? null,
    minutes: data.minutes ?? null,
    // Only a page that says so sets it; otherwise the app guesses (lib/vegetarian.ts).
    vegetarian: data.vegetarian ? true : null,
    ingredients: importedIngredients(data).map(({ name, amount, unit }) => ({ name, amount, unit })),
    steps: (data.steps ?? []).map(s => ({ text: s.text, minutes: s.minutes ?? null })),
    source: url == null ? null : data.source ?? 'web',
    rating_value: data.rating ? data.rating.value : null,
    rating_count: data.rating ? data.rating.count : null,
  }
}
